import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { MOUSEDOWN } from '../../utils/eventNames';

interface InputProps {
  timesSubmitted: Number;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  value?: string;
  placeholder?: string;
  id?: string;
  characterLimit?: number;
  handleCancel?: (event: any) => void;
  helperText?: string;
  handleChange?: (event: any) => void;
  onClick?: (event: any) => void;
}

export const TextArea: React.FC<InputProps> = ({
  timesSubmitted,
  label,
  error,
  disabled,
  className = '',
  value,
  placeholder,
  id,
  characterLimit,
  handleCancel,
  helperText,
  handleChange,
  onClick,
}) => {
  const [inactive, setInactive] = useState(true);
  const [errorAcknowledged, setErrorAcknowledged] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setErrorAcknowledged(false);
  }, [error, timesSubmitted]);

  const deactivateInput = useCallback(() => {
    setInactive(true);
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    if (!nodeRef.current?.contains(e.target as Node)) {
      deactivateInput();
    }
  }, [deactivateInput]);

  useEffect(() => {
    document.addEventListener(MOUSEDOWN, handleClick, false);
    return () => document.removeEventListener(MOUSEDOWN, handleClick, false);
  }, [handleClick]);

  const activateInput = () => {
    if (!disabled) {
      setInactive(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const acknowledgeError = () => {
    setErrorAcknowledged(true);
    setInactive(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleTab = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Tab') {
      const form = (event.target as HTMLTextAreaElement).form;
      const index = Array.prototype.indexOf.call(form, event.target);
      form.elements[index + 1].focus();
      event.preventDefault();
      deactivateInput();
    }
  };

  function renderHelperText() {
    if (helperText) {
      return <span className="helper-text">{helperText}</span>
    }
  }

  function renderCharacterLimit() {
    if (characterLimit) {
      return <div className="character-limit"><span>{value.length}/{characterLimit}</span></div>
    }
  }

  function renderErrorText() {
    if (error) {
      return <span className="error-text">{error}</span>
    }
  }

  function renderCancelSymbol() {
    if (!inactive && handleCancel) {
      return <div className="cancel" onClick={handleCancel}><i className="fas fa-times" /></div>
    }
  }

  const hasText = value ? 'has-text' : ''
  const inactiveOrActive = inactive ? 'inactive' : 'active'
  const sharedClasses = `input-container textfield-container ${hasText} ${inactiveOrActive} ${className}`

  if (error) {
    if (errorAcknowledged) {
      return (
        <div className="textarea-wrapper" onClick={onClick}>
          <div
            className={`error ${sharedClasses}`}
            onClick={activateInput}
            ref={nodeRef}
          >
            <label htmlFor={id}>{label}</label>
            <textarea
              disabled={disabled}
              id={id}
              maxLength={characterLimit ? characterLimit : 10000}
              onChange={handleChange}
              placeholder={placeholder}
              ref={inputRef}
              value={value}
            />
            {renderCancelSymbol()}
            {renderCharacterLimit()}
          </div>
        </div>
      )
    } else {
      return (
        <div className="textarea-wrapper" onClick={onClick}>
          <div
            className={`error unacknowledged ${sharedClasses}`}
            onClick={acknowledgeError}
            ref={nodeRef}
          >
            <label htmlFor={id}>{label}</label>
            <textarea
              id={id}
              maxLength={characterLimit ? characterLimit : 10000}
              onChange={handleChange}
              placeholder={placeholder}
              ref={inputRef}
              value={value}
            />
            {renderCancelSymbol()}
            {renderErrorText()}
            {renderCharacterLimit()}
          </div>
        </div>
      )
    }
  } else if (inactive) {
    return (
      <div className="textarea-wrapper" onClick={onClick}>
        <div
          className={sharedClasses}
          onClick={activateInput}
          ref={nodeRef}
        >
          <label htmlFor={id}>{label}</label>
          <textarea
            defaultValue={value}
            disabled={disabled}
            id={id}
            maxLength={characterLimit ? characterLimit : 10000}
            onFocus={activateInput}
            ref={inputRef}
          />
          {renderHelperText()}
          {renderCharacterLimit()}
        </div>
      </div>
    )
  } else {
    return (
      <div className="textarea-wrapper" onClick={onClick}>
        <div
          className={sharedClasses}
          ref={nodeRef}
        >
          <label htmlFor={id}>{label}</label>
          <textarea
            id={id}
            maxLength={characterLimit ? characterLimit : 10000}
            onChange={handleChange}
            onKeyDown={handleTab}
            placeholder={placeholder}
            ref={inputRef}
            value={value}
          />
          {renderCancelSymbol()}
          {renderCharacterLimit()}
        </div>
      </div>
    )
  }
};
